/**
 * Group Reward Template Stats Page
 * Displays statistics and history for a reward template
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
} from '@mui/material'
import {
  CardGiftcard,
  CheckCircle,
  Schedule,
  Redeem,
  SwapHoriz,
  People,
  Person,
  ArrowBack,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  useRewardTemplate,
  useTemplateStats,
  useRedemptionHistory,
  useTransferHistory,
} from '../hooks/useRewardTemplates'
import type { CurrentHolderDto } from '@api/types/rewards'
import { RewardInstanceStatus, getInstanceStatusName } from '@api/types/rewards'
import { getStatusColor } from '../utils/rewardHelpers'

interface StatsCardProps {
  icon: React.ReactNode
  label: string
  value: number
  color?: string
}

function StatsCard({ icon, label, value, color }: StatsCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '2px solid',
        borderColor: color || 'divider',
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: color || 'text.secondary', mb: 1 }}>{icon}</Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  )
}

export default function GroupRewardTemplateStatsPage() {
  const { id: groupId, templateId } = useParams<{ id: string; templateId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)

  const { data: template, isLoading: isLoadingTemplate } = useRewardTemplate(groupId, templateId)
  const { data: stats, isLoading: isLoadingStats } = useTemplateStats(groupId, templateId)
  const { data: redemptions, isLoading: isLoadingRedemptions } = useRedemptionHistory(groupId, templateId)
  const { data: transfers, isLoading: isLoadingTransfers } = useTransferHistory(groupId, templateId)

  if (isLoadingTemplate || isLoadingStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!template) {
    return (
      <Alert severity="error">Template not found</Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/groups/${groupId}/rewards`)}
        >
          Back
        </Button>
        <CardGiftcard color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {template.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Template Statistics
          </Typography>
        </Box>
      </Box>

      {/* Stats Overview */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<CardGiftcard fontSize="large" />}
              label="Distributed"
              value={stats.totalDistributed}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<CheckCircle fontSize="large" />}
              label="Active"
              value={stats.activeInstances}
              color="success.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<Redeem fontSize="large" />}
              label="Redeemed Uses"
              value={stats.totalRedeemedUses}
              color="info.main"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard
              icon={<Schedule fontSize="large" />}
              label="Expired"
              value={stats.expiredInstances}
              color="warning.main"
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<People />} label="Current Holders" iconPosition="start" />
          <Tab icon={<Redeem />} label="Redemptions" iconPosition="start" />
          <Tab icon={<SwapHoriz />} label="Transfers" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Current Holders */}
      {activeTab === 0 && stats?.currentHolders && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Claimed</TableCell>
                <TableCell>Remaining Uses</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.currentHolders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No current holders
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stats.currentHolders.map((holder: CurrentHolderDto, index: number) => (
                  <TableRow key={holder.userId || index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        {holder.displayName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(new Date(holder.claimedAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>{holder.remainingUses ?? 'Unlimited'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getInstanceStatusName(holder.status)}
                        size="small"
                        color={getStatusColor(holder.status)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Redemptions */}
      {activeTab === 1 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Redeemed By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Uses Consumed</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Override</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingRedemptions ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : redemptions?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No redemptions yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                redemptions?.items?.map((redemption) => (
                  <TableRow key={redemption.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        {redemption.scannerDisplayName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(new Date(redemption.redeemedAt), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>{redemption.usesConsumed}</TableCell>
                    <TableCell>
                      {redemption.latitude && redemption.longitude
                        ? `${redemption.latitude.toFixed(4)}, ${redemption.longitude.toFixed(4)}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {redemption.wasExpiredOverride ? (
                        <Chip label="Override" size="small" color="warning" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Transfers */}
      {activeTab === 2 && (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingTransfers ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : transfers?.items?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No transfers yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers?.items?.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        {transfer.fromUserDisplayName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          <Person />
                        </Avatar>
                        {transfer.toUserDisplayName || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {format(new Date(transfer.transferredAt), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
